import React, { useEffect } from 'react';
import { ThemeProvider, Theme, StyledEngineProvider, createTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import CssBaseline from '@mui/material/CssBaseline';
import { Notification } from 'react-admin';
import { useSelector } from 'react-redux';
import * as themes from '../theme';
import * as constants from '../../constants';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Fab from '@mui/material/Fab';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Zoom from '@mui/material/Zoom';
import { useDispatch } from 'react-redux';
import { SetLargeWindow } from '../configurations/actions';
import 'react-reflex/styles.css';
import Suspense from '../loading/suspense';
const DrawerMenu = React.lazy(() => import('./menu-drawer'));
const AppBar = React.lazy(() => import('./AppBar'));
const Paper = React.lazy(() => import('@mui/material/Paper'));
const Footer = React.lazy(() => import('./Footer'));

declare module '@mui/styles/defaultTheme' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}

declare module '@mui/styles/defaultTheme' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}

interface Props {
    /**
     * Injected by the documentation to work in an iframe.
     * You won't need it on your project.
     */
    window?: () => Window;
    children: React.ReactElement;
}

function areEqual(_prevProps: Props, _nextProps: Props) {
    /*
  return true if passing nextProps to render would return
  the same result as passing prevProps to render,
  otherwise return false
  */
    return true;
}

declare module '@mui/material/styles' {
    interface Theme {
        menu: {
            selected: {
                text: string;
                background: string;
            };
            nested: {
                background: string;
            };
        };
        status: {
            danger: string;
        };
    }
    // allow configuration using `createTheme`
    interface ThemeOptions {
        menu: {
            selected: {
                text: string;
            };
            nested: {
                background: string;
            };
        };
        status?: {
            danger?: string;
        };
    }
}
export const hexToRGB = (hex: string, alpha: string) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    if (alpha) {
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    return `rgb(${r}, ${g}, ${b})`;
};

const ColorModeContext = React.createContext({ toggleColorMode: () => {} });

/*
 * DO NOT USE UNNECESSARY PROPS AND useSelector IN PARENT COMPONENT ==> CAUSE RE-RENDERING ALL CHILDREN
 */
function Layout(props: Props) {
    const drawerWidth: number = 240;
    const largeWindow = useSelector((state: constants.AppState) => state.largeWindow);

    const font = useSelector((state: constants.AppState) => state.font);
    const direction = useSelector((state: constants.AppState) => state.direction);
    const themeName = useSelector((state: constants.AppState) => state.theme);
    const rootDiv = document.getElementById('root');
    const dispatch = useDispatch();
    const drawerVariant = largeWindow === true ? 'permanent' : 'temporary';
    const [open, setOpen] = React.useState(drawerVariant === 'permanent' ? true : false);

    const [mode, setMode] = React.useState<'light' | 'dark'>('light');
    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode(prevMode => (prevMode === 'light' ? 'dark' : 'light'));
            },
        }),
        [mode],
    );

    const theme = React.useMemo(
        () =>
            createTheme({
                ...themes[themeName],
                direction: direction,
                typography: {
                    fontFamily: [font].join(','),
                },
                palette: {
                    ...(themes as any)[themeName]['palette'],
                    mode,
                },
                menu: {
                    ...(themes as any)[themeName]['menu'],
                },
            }),
        [mode, themeName],
    );

    const useStyles = makeStyles(() => ({
        root: {
            display: 'flex',
            direction: direction,
            height: largeWindow === true ? '100%' : '',
        },
        title: {
            textAlign: 'center',
            width: '100%',
        },

        drawer: {
            width: drawerWidth,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            '& .MuiPaper-root': {
                right: direction == 'rtl' ? 0 : 'initial',
                left: direction == 'ltr' ? 0 : 'initial',
            },
        },
        drawerOpen: {
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
        drawerClose: {
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            overflowX: 'hidden',
            width: theme.spacing(7) + 1,
            [theme.breakpoints.up('sm')]: {
                width: constants.LAYOUT.SIDEBAR_MENU.WIDTH, //theme.spacing(9) + 1,
            },
        },
        toolbar: {
            display: 'flex',
            alignItems: 'center',
            direction: direction,
            justifyContent: 'flex-end',
            padding: theme.spacing(0, 1),
            //   ...theme.mixins.toolbar,
            minHeight: '60px',
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
        },
        content: {
            flexGrow: 1,
            padding: theme.spacing(0),
            //paddingTop: constants.LAYOUT.APPBAR.HEIGHT,
            height: '100vh',
            //  height: largeWindow === true ? '100vh' : "",
            paddingLeft: 0, //largeWindow === true ? theme.spacing(0) : theme.spacing(0.5),
            paddingRight: 0, //largeWindow === true ? theme.spacing(0) : theme.spacing(0.5),
            // paddingBottom: theme.spacing(1),
            //width: '100%',
            width:
                open === false && drawerVariant === 'permanent'
                    ? `calc(100% - ${constants.LAYOUT.SIDEBAR_MENU.WIDTH}px)`
                    : open === true && drawerVariant === 'permanent'
                    ? `calc(100% - ${constants.LAYOUT.SIDEBAR_MENU.DRAWER_WIDTH}px)`
                    : '100%',
        },
        gridRoot: {
            flexGrow: 1,
            height: largeWindow === true ? '100vh' : '',
            direction: direction,
        },
        paper: {
            //      padding: theme.spacing(2),
            textAlign: 'center',
            color: theme.palette.text.secondary,
            padding: 2,
            // width: (open === false && largeWindow === true) ? `calc(100% - ${constants.LAYOUT.SIDEBAR_MENU.WIDTH}px)` : ( (open === true && largeWindow === true) ? `calc(100% - ${constants.LAYOUT.SIDEBAR_MENU.DRAWER_WIDTH}px)` : '100%'),
            //width: (open === false && largeWindow === true) ? `100%` : ( (open === true && largeWindow === true) ? `100%` : '100%'),
            //paddingBottom: constants.LAYOUT.FOOTER.HEIGHT * 4,
            background: theme.palette.grey['100'] + ' !important',

            //height: largeWindow === true ? '100vh' : "",
            height: `calc(100% - ${constants.LAYOUT.APPBAR.HEIGHT + constants.LAYOUT.FOOTER.HEIGHT}px)`,
            overflow: 'auto',
            //background: 'red !important'
        },
        scrollTop: {
            position: 'fixed',
            bottom: theme.spacing(4),
        },
        scrollTopRight: {
            right: theme.spacing(2),
        },
        scrollTopLeft: {
            left: theme.spacing(2),
        },
        flexDirection: {},
        backdrop: {
            zIndex: theme.zIndex.drawer + 1,
            color: '#fff',
        },
    }));
    const classes = useStyles();

    const handleDrawerClose = React.useCallback(() => {
        setOpen(false);
    }, [open]);

    const handleDrawerToggle = React.useCallback(() => {
        setOpen(!open);
    }, [open]);

    function ScrollTop(props: Props) {
        const { children, window } = props;
        // Note that you normally won't need to set the window ref as useScrollTrigger
        // will default to window.
        // This is only being set here because the demo is in an iframe.
        const trigger = useScrollTrigger({
            target: window ? window() : undefined,
            disableHysteresis: true,
            threshold: 100,
        });

        const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
            const anchor = ((event.target as HTMLDivElement).ownerDocument || document).querySelector(
                '#back-to-top-anchor',
            );

            if (anchor) {
                anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        };

        var side = direction === 'rtl' ? classes.scrollTopLeft : classes.scrollTopRight;

        return (
            <Zoom in={trigger}>
                <div onClick={handleClick} role="presentation" className={`${classes.scrollTop} ${side}`}>
                    {children}
                </div>
            </Zoom>
        );
    }

    function handleResize() {
        if (window.innerWidth > 960) {
            dispatch(SetLargeWindow(true));
            document.body.classList.add('largeBody');
            if (rootDiv != null) rootDiv.classList.add('largeBody');
        } else {
            dispatch(SetLargeWindow(false));
            document.body.classList.remove('largeBody');
            if (rootDiv != null) rootDiv.classList.remove('largeBody');
        }
    }

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        handleResize();
    }, []);

    useEffect(() => {}, [largeWindow]);

    window.onbeforeunload = function(e: any) {
        e.preventDefault();
        return 'Please press the Logout button to logout.';
    };

    const { children } = props;

    //console.log('rerendering layout....');
    return (
        <StyledEngineProvider injectFirst>
            <ColorModeContext.Provider value={colorMode}>
                <ThemeProvider theme={theme}>
                    <div id="back-to-top-anchor" />
                    <div className={classes.root}>
                        <Suspense>
                            <DrawerMenu
                                open={open}
                                handleDrawerToggle={handleDrawerToggle}
                                handleDrawerClose={handleDrawerClose}
                                drawerVariant={drawerVariant}
                            />
                        </Suspense>

                        <main id="main-content" className={`${classes.content}`}>
                            <CssBaseline />
                            <Suspense>
                                <AppBar handleDrawerToggle={handleDrawerToggle} open={open} />
                            </Suspense>

                            <Suspense>
                                <Paper className={`${classes.paper}`} /*style={{paddingBottom: theme.spacing(15)}}*/>
                                    {children}
                                </Paper>
                            </Suspense>

                            <ScrollTop {...props}>
                                <Fab color="secondary" size="small" aria-label="scroll back to top">
                                    <KeyboardArrowUpIcon />
                                </Fab>
                            </ScrollTop>
                        </main>
                    </div>
                    <Suspense>
                        <Footer />
                    </Suspense>

                    {/*
            https://github.com/pvoznyuk/react-live-clock
            https://material-ui-pickers.dev/localization/calendar-systems
    */}
                    <Notification style={{ direction: direction }} key={1} />
                </ThemeProvider>
            </ColorModeContext.Provider>
        </StyledEngineProvider>
    );
}
export default React.memo(Layout, areEqual);
